import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import {
  type MultiversionTestRunVersionResults,
  RouterMultiversionTestRun,
  type TestRunVersionProgress,
} from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import { executionResultsToProgress } from "src/api/testRuns/progress";
import { GraphRunResultType } from "src/api/types";

const POLLING_INTERVAL_MS = 2000;

/**
 * `expired` is the workspace's 10-minute cap on a dataset test run: the run
 * API answers 408 and the run is gone, so there is nothing left to poll.
 */
export type TestRunProgressStatus =
  | "running"
  | "finished"
  | "failed"
  | "expired"
  /** The run API could not be read; the run's real state is unknown. */
  | "unavailable";

/** States the run cannot leave, and so the only ones that stop the poll. */
const SETTLED_STATUSES: ReadonlySet<TestRunProgressStatus> = new Set([
  "finished",
  "failed",
  "expired",
]);

export type TestRunProgressState = {
  status: TestRunProgressStatus;
  progress: TestRunVersionProgress | null;
};

const isSettled = (state: TestRunProgressState | undefined): boolean =>
  state !== undefined && SETTLED_STATUSES.has(state.status);

export const testRunProgressKeys = {
  detail: (workspaceId: string | null, testRunId: string | null) =>
    ["unified-copilot", workspaceId, "test-runs", testRunId] as const,
};

/**
 * The copilot starts one version per dataset run, so an unknown
 * `flowVersionId` resolves to the run's only version.
 */
const selectVersion = (
  versions: MultiversionTestRunVersionResults[],
  flowVersionId: string | null,
): MultiversionTestRunVersionResults | undefined =>
  (flowVersionId === null
    ? undefined
    : versions.find((version) => version.id === flowVersionId)) ?? versions[0];

const versionToState = (
  version: MultiversionTestRunVersionResults | undefined,
): TestRunProgressState => {
  const executionResults = version?.execution_results ?? null;
  if (executionResults === null) {
    return { status: "running", progress: version?.progress ?? null };
  }
  return {
    status:
      executionResults.type === GraphRunResultType.ERROR
        ? "failed"
        : "finished",
    progress: version?.progress ?? executionResultsToProgress(executionResults),
  };
};

/**
 * Follows a dataset test run straight from the run API, independent of the
 * copilot session — the session only supplies the run's id.
 */
export const useTestRunProgress = ({
  testRunId,
  flowVersionId,
  enabled = true,
}: {
  testRunId: string | null;
  flowVersionId: string | null;
  enabled?: boolean;
}): { state: TestRunProgressState; isPending: boolean } => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;

  const query = useQuery({
    queryKey: testRunProgressKeys.detail(workspace?.id ?? null, testRunId),
    queryFn: async (): Promise<TestRunProgressState> => {
      if (!baseUrl || testRunId === null) {
        throw new Error("Test run progress requires a workspace and a run id");
      }
      try {
        const response = await RouterMultiversionTestRun.get(
          baseUrl,
          testRunId,
        );
        return versionToState(
          selectVersion(response.data.flow_versions, flowVersionId),
        );
      } catch (error) {
        // An expired run is a settled outcome, not a failure to retry.
        if (isAxiosError(error) && error.response?.status === 408) {
          return { status: "expired", progress: null };
        }
        throw error;
      }
    },
    enabled: enabled && baseUrl !== undefined && testRunId !== null,
    // The poll is the retry: one clean attempt per tick, rather than React
    // Query's backoff stalling recovery behind its own schedule.
    retry: false,
    // Keep polling while the run is unsettled — including after a failed
    // read, which left no data at all. Stopping there would freeze the card
    // on a status it only assumed.
    refetchInterval: (currentQuery) =>
      isSettled(currentQuery.state.data) ? false : POLLING_INTERVAL_MS,
    // Reading a settled run again can only lose information: past the
    // workspace's 10-minute cap the run API answers 408 for it, which reads
    // as `expired` and carries no counts. So every refetch trigger has to
    // respect a settled outcome, not just the interval — a card outlives its
    // own mount, since reopening the conversation mounts it again.
    refetchOnMount: (currentQuery) => !isSettled(currentQuery.state.data),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // A settled outcome is immutable and the run API stops serving it, so the
    // cache is the only place it survives. Keeping it for the tab's life is
    // what lets a reopened conversation show the real result.
    gcTime: Infinity,
    throwOnError: false,
  });

  return {
    state:
      query.data ??
      (query.isError
        ? // Say the run cannot be read, rather than claiming it is running.
          { status: "unavailable", progress: null }
        : // Before the first response there are no counts to show, so the bar
          // renders empty rather than jumping once real numbers arrive.
          { status: "running", progress: null }),
    isPending: query.data === undefined && !query.isError,
  };
};
